import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import helpers from "helpers/index";
import queryClient, { IQueryParams } from 'queries';
import { TypedVehicle } from "./vehicle.query";
import { TypedTripData } from "./trip.query";
import { TypedSeating } from "./seating.query";
import { TypedUser } from "interface/user.model";
import { TypedTrip_manifest_fee } from "./trip_manifest_fee.query";
import { TypedDriver_settlement } from "./driver_settlement.query";
import { TypedOrders } from "./orders.query";
import { TypedInvoices } from "./invoices.query";
import { TypedVehicle_log } from "./vehicle_log.query";

export interface TypedTripManifestDispatchJourney {
    id: string;
    journey_name: string;
    journey_from: string;
    journey_to: string;
    journey_type: 'outbound' | 'inbound';
}

export interface TypedTripManifestDispatchVehicle {
    id: string;
    name: string;
    license_plate: string;
}

export interface TypedTripManifestDispatchDriver {
    trip_manifest_driver_id: string;
    driver_id: string;
    user_id: string;
    display_name: string;
    user_phonenumber: string;
}

export interface TypedTripManifestDispatchTrip {
    id: string;
    time: string;
    departure_date: string;
    trip_manifest_id: string;
    trip_status: ENUM_TRIP_MANIFEST_STATUS;
    transportation_type: string | null;
    vehicle: TypedTripManifestDispatchVehicle | null;
    driver: TypedTripManifestDispatchDriver | null;
}

export interface TypedTripManifestDispatchDirection {
    journey: TypedTripManifestDispatchJourney;
    trips: TypedTripManifestDispatchTrip[];
}

/** Dữ liệu bảng điều xe theo cặp tuyến và ngày khởi hành. */
export interface TypedTripManifestDispatchData {
    route_pair_id: string;
    departure_date: string;
    outbound: TypedTripManifestDispatchDirection;
    inbound: TypedTripManifestDispatchDirection;
}

// BƯỚC 1: Định nghĩa và export type cho một vé
export type Typed_order_meta = {
    "id": string,
    "trip_manifest_id": string,
    "order_id": string,
    "call_status": ENUM_CALL_STATUS,
    "call_last_updated": bigint,
    "call_last_updatedBy": bigint,

    "order_status": ENUM_TICKET_STATUS,
    "order_status_last_updated": bigint,
    "order_status_last_updatedBy": bigint,

    "pickup_by"?: bigint,
    "pickup_at"?: bigint,
    "dropoff_by"?: bigint,
    "dropoff_at"?: bigint,

    "createdAt": string,

    order_status_last_updated_author_data?: TypedUser
    call_last_updated_author_data?: TypedUser

    pickup_by_data?: TypedUser
    dropoff_by_data?: TypedUser

};

export interface TypedTripManifestDriver {
    "id"?: string,
    "slug"?: string,
    "trip_manifest_id"?: string,
    "driver_id"?: bigint,
    "driver_role"?: string, // driver: lái chính hoặc assistant: lái phụ
    "createdAt"?: string,
    "driver_data"?: {
        "user_id"?: bigint,
        "display_name"?: string,
        "user_phonenumber"?: string
    }
}

export enum ENUM_TRIP_MANIFEST_STATUS {
    DRAFT = 0,
    DISPATCHED = 1,
    IN_TRANSIT = 2,
    COMPLETED = 3,
    FINALIZED = 4,
    CANCELLED = 9,
}

// Đối tượng này ánh xạ enum sang chuỗi văn bản tương ứng
export const tripStatusLabels: { [key in ENUM_TRIP_MANIFEST_STATUS]: string } = {
    [ENUM_TRIP_MANIFEST_STATUS.DRAFT]: 'Bản nháp',
    [ENUM_TRIP_MANIFEST_STATUS.DISPATCHED]: 'Đã điều chuyến',
    [ENUM_TRIP_MANIFEST_STATUS.IN_TRANSIT]: 'Đang chạy',
    [ENUM_TRIP_MANIFEST_STATUS.COMPLETED]: 'Hoàn thành',
    [ENUM_TRIP_MANIFEST_STATUS.FINALIZED]: 'Đã chốt lệnh',
    [ENUM_TRIP_MANIFEST_STATUS.CANCELLED]: 'Hủy chuyến',
};
export const tripStatusHelptext: { [key in ENUM_TRIP_MANIFEST_STATUS]: string } = {
    [ENUM_TRIP_MANIFEST_STATUS.DRAFT]: 'Chỉ admin nhìn thấy bản nháp',
    [ENUM_TRIP_MANIFEST_STATUS.DISPATCHED]: 'Tài xế sẽ nhận được thông báo',
    [ENUM_TRIP_MANIFEST_STATUS.IN_TRANSIT]: 'Tài xế đã bắt đầu chuyến',
    [ENUM_TRIP_MANIFEST_STATUS.COMPLETED]: 'Tài xế đánh dấu là hoàn thành chuyến',
    [ENUM_TRIP_MANIFEST_STATUS.FINALIZED]: 'Tài xế xác nhận đã kiểm tra và chốt lệnh',
    [ENUM_TRIP_MANIFEST_STATUS.CANCELLED]: '',
};

export enum ENUM_CALL_STATUS {
    UNTOUCHED = 'UNTOUCHED',
    NOT_CALLED = 'NOT_CALLED',
    INVALID_NUMBER = 'INVALID_NUMBER',
    COMPLETED = 'COMPLETED',
    NO_ANSWER = 'NO_ANSWER',
}

export const enum ENUM_TICKET_STATUS {
    AWAITING_PICKUP = 'AWAITING_PICKUP',
    PICKED_UP = 'PICKED_UP',
    DROPPED_OFF = 'DROPPED_OFF',
    CANCELLED = 'CANCELLED'
}

export interface TypedTripManifest {
    id?: string;
    time_id?: string;
    departure_date?: string;
    departure_hour?: string;
    seating_id?: string;
    vehicle_id?: string;
    /**
     * trip_status bao gồm: 0: bản nháp, 1: đã điều chuyến, 2: đang trong chuyến, 3: Hoàn thành, 9: hủy chuyến, không thực hiện
     */
    trip_status?: ENUM_TRIP_MANIFEST_STATUS;
    /**
     * trip_type: mainline là chuyến chính, shuttle là chuyến chung chuyển, đi gom khách khắp nơi xong về chuyến chính ...
     */
    trip_type?: string;
    trip_manifest_parent?: string;
    createdBy?: string;
    createdAt?: string;
    arrivedOn?: string;

    total_fare?: number,
    total_fee?: number,
    total_amount_due?: number;
    bonus_amount?: number; /** Thưởng theo chuyến, tùy giám đốc */

    total_occupied_seats?: number,
    total_occupied_goods?: number,

    driver_settlement?: TypedDriver_settlement; // bảng nối với driver_settlement
    "seating"?: TypedSeating,
    "trip"?: TypedTripData,
    "vehicle"?: TypedVehicle,
    trip_manifest_driver?: TypedTripManifestDriver[],
    trip_manifest_fee?: TypedTrip_manifest_fee[],

    orders?: TypedOrders[];
    vehicle_log?: TypedVehicle_log;

}

type IQuery = TypedTripManifest & IQueryParams;


export interface TypedDriverVisualizationUser {
    user_id: string;
    user_phonenumber: string | null;
    display_name: string | null;
}

export interface TypedDriverVisualizationFee {
    trip_fee: number;
    trip_fee_description: string | null;
}

export interface TypedDriverVisualizationOrder {
    order_code: string;
    pickup_address: string | null;
    dropoff_address: string | null;
    total_fee: number;
    total_must_pay: number;
    total_seat: number;
    order_status: number;
    payment_status: number;
    payment_method: string;
    invoices: TypedInvoices;
    customer_data: TypedDriverVisualizationUser;
}

export interface TypedDriverVisualizationTripManifest {
    id: string;
    driver_settlement?: TypedDriver_settlement;

    vehicle: string;
    journey_name: string;
    journey_id: string;
    time_id: string;
    trip_status: number;
    departure_date: number;
    departure_hour: string;
    total_fare: number;
    total_fee: number;
    total_amount_due: number;
    total_occupied_seats: number;
    total_occupied_good: number;
    trip_manifest_fee: TypedDriverVisualizationFee[];
    order_data: TypedDriverVisualizationOrder[];
}

export interface TypedDriverAndTripManifestData {
    driver: TypedDriverVisualizationUser;
    /** lấy toàn bộ tài phụ ra */
    assistant: TypedDriverVisualizationUser[];
    trip_manifests: TypedDriverVisualizationTripManifest[];
}

export interface TypedDriverVisualizationResult {
    data: TypedDriverAndTripManifestData[];
    transaction_data: {
        total_trip_manifest_fee: number,
        total_payment_bank: number,
        total_payment_cash: number,
        total_payment_transaction: number,
        total_expected_paid: number,
        total_debit: number,
        total_unpaid: number,
    }
}


/** Hiển thị tại trang chủ, hiển thị trực quan theo ngày thôi ... */
export function useGetTrip_manifests_by_driver(yyyymmdd: string) {
    return useQuery({
        queryKey: ["trip_manifest/driver_visualization", yyyymmdd],
        queryFn: () => axios.get<TypedDriverVisualizationResult>(`/trip_manifest/driver_visualization/${yyyymmdd}`).then(response => response.data),
    });
}


/*
* List of all trip manifest
*/
export function useGetTrip_manifests(object: IQuery) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["trip_manifest/fetch_entity_list"],
        queryFn: () => axios.get<TypedTripManifest[]>(`/trip_manifest/${EndURL}`).then(response => response.data),
        retry: 1,
    });
}


/**
 * Lấy thông tin chuyến đi cụ thể theo manifest_id, để hiển thị chi tiết chuyến đi
 * @param manifest_id 
 * @returns 
 */
export function useGetTrip_manifest(manifest_id: any) {
    return useQuery({
        queryKey: ["trip_manifest/fetch_entity", manifest_id],
        queryFn: () => axios.get<TypedTripManifest>(`/trip_manifest/${manifest_id}`).then(response => response.data),
        retry: 1,
        enabled: !!manifest_id
    });
}


/** lấy danh sách invoice thông qua vehicle id */
export function useGetInvoicesViaVehicleID(object: IQuery) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["trip_manifest/fetch_invoice_for_vehicle", object],
        queryFn: () => axios.get<any>(`/trip_manifest/invoices/${EndURL}`).then(response => {
            let { data, headers } = response;
            return {
                body: data,
                totalItems: Number(headers['x-total-count'] || 0)
            }
        }),
    });
}

/** Tải về excel bản invoice của một xe đang xem */
export function useDownloadInvoicesViaVehicleID() {
    return useMutation({
        mutationKey: ["trip_manifest/download_invoice"],
        mutationFn: (object: IQuery) => axios.get<Blob>(`/trip_manifest/invoices/download/${helpers.buildEndUrl(object)}`, { responseType: 'blob', }).then(response => response.data),
    })
}



/** cho trang chủ, thu tiền, tình trạng thu tiền và số tiền trong 7 ngày gần nhất */
export function useGetTrip_manifest_due_date() {
    return useQuery({
        queryKey: ["trip_manifest/fetch_entity_due_date"],
        queryFn: () => axios.get<{
            date: string,
            due: string,
            collected: string,
            key: string,
        }[]>(`/trip_manifest/due_date`).then(response => response.data),
    });
}


/** Danh sách điều xe và tài xế */
export function useGetTrip_manifest_dispatch_list(object: {
    departure_date: string,
    journey_id: string,
    enabled: boolean,
}) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["trip_manifest/dispatch"],
        queryFn: () => axios.get<TypedTripManifestDispatchData>(`/trip_manifest/dispatch/${EndURL}`).then(response => response.data),
        enabled: Boolean(object.enabled || false)
    });
}


export function useMutateGetTrip_manifest() {
    return useMutation({
        mutationKey: ["trip_manifest/fetch_entity_list"],
        mutationFn: (object: IQuery) => axios.get<TypedTripManifest[]>(`/trip_manifest/${helpers.buildEndUrl(object)}`).then(response => {
            let { data, headers } = response;
            return {
                body: data,
                totalItems: Number(headers['x-total-count'] || 0)
            }
        }),
        retry: 1,
    });
}

/** Cho truy vấn các chuyến của một lái xe, trong màn hình analytics chi tiết một lái xe ... */
export function useGetTrip_manifests_list(object) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["trip_manifest/fetch_entity_list_for_list", object],
        queryFn: () => axios.get<TypedTripManifest[]>(`/trip_manifest/list/${EndURL}`).then(response => {
            let { data, headers } = response;
            return {
                body: data,
                totalItems: Number(headers['x-total-count'] || 0)
            }
        }),
        enabled: !!object
    });
}

export function useMutateUpdateTrip_manifest() {
    return useMutation({
        mutationKey: ["trip_manifest/update_entity"],
        mutationFn: ({ id, ...rest }: TypedTripManifest) => axios.patch<TypedTripManifest>(`/trip_manifest/${id}`, rest).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        },
        retry: 1,
    });
}

export function useMutateUpdateTrip_manifest_status() {
    return useMutation({
        mutationKey: ["trip_manifest/update_entity_status"],
        mutationFn: ({ id, ...rest }: TypedTripManifest) => axios.patch<TypedTripManifest>(`/trip_manifest/update_status/${id}`, rest).then(response => response.data),
        onSuccess: () => {
            // queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        },
        retry: 1,
    });
}

export function useMutateUpdateTrip_manifest_bonus() {
    return useMutation({
        mutationKey: ["trip_manifest/update_entity_bonus"],
        mutationFn: ({ id, ...rest }: any) => axios.patch<any>(`/trip_manifest/update_bonus/${id}`, rest).then(response => response.data),
        onSuccess: () => {
            // queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        },
        retry: 1,
    });
}

export function useMutateUpdateTrip_manifest_seating_template() {
    return useMutation({
        mutationKey: ["trip_manifest/update_entity_seating_template"],
        mutationFn: ({ id, ...rest }: TypedTripManifest) => axios.patch<TypedTripManifest>(`/trip_manifest/update_seating_template/${id}`, rest).then(response => response.data),
        onSuccess: () => {
            // queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['ticket/fetch_unassigned_list'] }); // reset lại cái danh sách vé chưa điều chuyến
            // queryClient.invalidateQueries({ queryKey: ['ticket/fetch_entity_list'] }); // reset lại cái danh sách vé để hiển thị
        },
        retry: 1,
    });
}

/** 
 * Tạo tối thiểu: Post lên 
 * {
    "time_id": 160, // id của time_id
    "trip_type": "mainline" // hoặc shuttle
}
 */


export function useMutateCreateTrip_manifest() {
    return useMutation({
        mutationKey: ['trip_manifest/create_entity'],
        mutationFn: (body: TypedTripManifest) => axios.post(`/trip_manifest`, body).then((response) => response.data),
        onSuccess: () => {
            /** Load lại trip */
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        }
    });
}

/** Chỉ xóa bản nháp thôi nhá */
export function useMutateDeleteTrip_manifest() {
    return useMutation({
        mutationKey: ['trip_manifest/delete_entity'],
        mutationFn: (trip_manifest_id: string) => axios.delete(`/trip_manifest/${trip_manifest_id}`).then((response) => response.data),
        onSuccess: () => {
            /** Load lại trip */
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        }
    });
}



/** Tải về excel bản đang xem */
export function useDownloadCurrentTripManifest() {
    return useMutation({
        mutationKey: ["trip_manifest/download"],
        mutationFn: (object) => axios.get<Blob>(`/trip_manifest/download${helpers.buildEndUrl(object)}`, { responseType: 'blob', }).then(response => response.data),
    })
}


/**** Các chức năng khác ... */




/***********************
 * 
 * CHỖ NÀY NÊN THÔNG QUA SOCKET ĐỂ LOAD LẠI CHUYẾN ...
 * CHƯA LÀMMMMMM
 * 
 */

/** THÊM Tài vào chuyến */
// {
// "trip_manifest_id": 1,
//     "driver_id": 2,
//     "driver_role": "assistant" // driver
// }
export function useMutateAssign_driver_to_trip() {
    return useMutation({
        mutationKey: ['trip_manifest_driver/create_entity'],
        mutationFn: (body: { trip_manifest_id: string, driver_id: string, driver_role: string }) => axios.post(`/trip_manifest_driver`, body).then((response) => response.data),
        onSuccess: () => {
            /** Load lại trip */
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        }
    });
}

/** Xóa tài xế đi  */
export function useMutateRemove_driver_from_trip() {
    return useMutation({
        mutationKey: ['trip_manifest_driver/delete_entity'],
        mutationFn: (trip_manifest_driver_id: string) => axios.delete<any>(`/trip_manifest_driver/${trip_manifest_driver_id}`).then(response => response.data),
        onSuccess: () => {
            // queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        }
    });
}

/** *********************************** */
